import React, { ReactElement, ReactNode, useContext, useEffect } from 'react'
import {
  BrowserRouter,
  Navigate,
  Route,
  RouteProps,
  Routes as ReactRoutes,
  useLocation,
} from 'react-router-dom'
import PrivateRoute from './components/PrivateRoute'
import FullScreenSpinner from 'components/FullScreenSpinner'
import Sign, { SignType } from 'pages/Sign'
import CampaignForm from 'pages/Campaign/CampaignForm'
import CampaignDetails from 'pages/Campaign/CampaignDetails'
import CampaignAcceptanceFields from 'pages/Campaign/CampaignAcceptanceFields'
import { Routes } from 'routes'
import { AppContext } from 'contexts/AppContext'
import ScrollToTop from 'components/ScrollToTop'
import { NotifierQueue } from 'components/Notifier'
import Layout from 'components/Layout'
import PageNotFound from 'pages/PageNotFound'
import Agencies from 'pages/Agencies'
import { FormType } from 'types/various'
import AgencyForm from 'pages/Agencies/AgencyForm'
import UserSettings from 'pages/User/UserSettings'
import Report from 'pages/Report'
import UserTable from 'pages/User/UserTable'
import UserEdit from 'pages/User/UserEdit'
import UserInvite from 'pages/User/UserInvite'
import Inventory from 'pages/Inventory'
import { PAGES } from 'constant/authorization'
import Features from 'pages/Features'
import CampaignTable from 'pages/Campaign/CampaignTable'
import RolesTable from './pages/Roles/RolesTable'
import RoleForm from './pages/Roles/RoleForm'
import { CampaignPermissionType, FeatureName, PermissionType } from './types/features'
import EmissionPlan from './pages/EmissionPlan'
import EmissionSchedule from './pages/Campaign/EmissionSchedule/EmissionSchedule'
import Broadsign from './pages/BroadSign'
import Bundles from './pages/Bundle'
import ReactGA from 'react-ga4'
import VoohCampaignTable from './pages/VoohCampaign/VoohCampaignTable/VoohCampaignTable'
import VoohCampaignForm from './pages/VoohCampaign/VoohCampaignForm/VoohCampaignForm'
import VoohEmissionSchedule from './pages/VoohCampaign/VoohEmissionSchedule/VoohEmissionSchedule'
import VoohCampaignDetails from './pages/VoohCampaign/VoohCampaignDetails/VoohCampaignDetails'
import CampaignDetailsTabs from './pages/Campaign/CampaignDetailsTabs'
import AICreationUpload from './pages/AICreationUpload'

const App: React.FC = () => {
  const { homepageUrl, userData, notifications, allowedFor } = useContext(AppContext)

  useEffect(() => {
    if (process.env.REACT_APP_GA_TRACKING_ID) {
      ReactGA.initialize(process.env.REACT_APP_GA_TRACKING_ID)
    }
  }, [])

  const GoogleAnalytics = () => {
    const location = useLocation()

    useEffect(() => {
      if (process.env.REACT_APP_GA_TRACKING_ID) {
        // we want only to track production
        ReactGA.send({ hitType: 'pageview', page: location.pathname + location.search })
      }
    }, [location])

    return <></>
  }

  const UtilityRoutes = () => {
    const CampaignTablePrivateRoute: ReactElement = (
      <PrivateRoute
        allowed={allowedFor({
          feature: { name: FeatureName.Campaigns, action: CampaignPermissionType.Read },
        })}
      >
        <CampaignTable />
      </PrivateRoute>
    )
    const PageNotFoundPrivateRoute: ReactElement = (
      <PrivateRoute allowed={allowedFor({ template: PAGES.NOT_FOUND })}>
        <PageNotFound />
      </PrivateRoute>
    )

    return (
      <>
        <Route path={Routes.ROOT}>
          <Route
            path={''}
            element={
              <Navigate
                to={homepageUrl}
                replace={true}
              />
            }
          />
          <Route
            path={Routes.HOME}
            element={
              <Navigate
                to={homepageUrl}
                replace={true}
              />
            }
          />
          <Route
            path={Routes.CAMPAIGNS.ALL}
            element={CampaignTablePrivateRoute}
          />
        </Route>
        <Route
          path={Routes.ANY}
          element={PageNotFoundPrivateRoute}
        />
        <Route
          path={Routes.NOT_FOUND}
          element={PageNotFoundPrivateRoute}
        />
      </>
    )
  }

  const SignRoutes = () => {
    const elementOrHomeIfSignedIn = (el: ReactNode): RouteProps['element'] =>
      userData.signedIn ? <Navigate to={Routes.HOME} /> : el

    return (
      <>
        <Route
          path={Routes.SIGN.SIGN_IN}
          element={elementOrHomeIfSignedIn(<Sign type={SignType.SIGN_IN} />)}
        />
        <Route
          path={Routes.SIGN.REMIND_PASSWORD}
          element={elementOrHomeIfSignedIn(<Sign type={SignType.REMIND_PASSWORD} />)}
        />
        <Route
          path={Routes.SIGN.RESET_PASSWORD}
          element={elementOrHomeIfSignedIn(<Sign type={SignType.RESET_PASSWORD} />)}
        />
        <Route
          path={Routes.SIGN.ACCEPT_INVITATION}
          element={elementOrHomeIfSignedIn(<Sign type={SignType.ACCEPT_INVITATION} />)}
        />
      </>
    )
  }

  const AgencyRoutes = () => (
    <>
      <Route
        path={Routes.AGENCIES.ALL}
        element={
          <PrivateRoute
            allowed={allowedFor({
              template: PAGES.AGENCIES.LIST,
              feature: { name: FeatureName.Agencies, action: PermissionType.Read },
            })}
          >
            <Agencies />
          </PrivateRoute>
        }
      />
      <Route
        path={Routes.AGENCIES.NEW}
        element={
          <PrivateRoute
            allowed={allowedFor({
              template: PAGES.AGENCIES.ADD_EDIT,
              feature: { name: FeatureName.Agencies, action: PermissionType.Write },
            })}
          >
            <AgencyForm />
          </PrivateRoute>
        }
      />
      <Route
        path={Routes.AGENCIES.EDIT(':id')}
        element={
          <PrivateRoute
            allowed={allowedFor({
              template: PAGES.AGENCIES.ADD_EDIT,
              feature: { name: FeatureName.Agencies, action: PermissionType.Write },
            })}
          >
            <AgencyForm />
          </PrivateRoute>
        }
      />
    </>
  )

  const CampaignRoutes = () => {
    const EmissionSchedulePrivateRoute: ReactElement = (
      <PrivateRoute allowed={allowedFor({ template: PAGES.CAMPAIGNS.EMISSION_SCHEDULE })}>
        <EmissionSchedule />
      </PrivateRoute>
    )

    return (
      <>
        <Route
          path={Routes.CAMPAIGNS.COMPARE}
          element={
            <PrivateRoute allowed={allowedFor({ template: PAGES.CAMPAIGNS.DETAILS })}>
              <CampaignDetailsTabs />
            </PrivateRoute>
          }
        />
        <Route
          path={Routes.CAMPAIGNS.COMPARE_PUBLIC}
          element={
            <Layout>
              <CampaignDetailsTabs publicView />
            </Layout>
          }
        />
        <Route
          path={Routes.CAMPAIGNS.ONE(':id')}
          element={
            <PrivateRoute allowed={allowedFor({ template: PAGES.CAMPAIGNS.DETAILS })}>
              <CampaignDetails />
            </PrivateRoute>
          }
        />
        <Route
          path={Routes.CAMPAIGNS.ONE_PUBLIC(':uuid')}
          element={
            <Layout>
              <CampaignDetails publicView />
            </Layout>
          }
        />
        <Route
          path={Routes.EMISSION_PLAN(':uuid')}
          element={
            <Layout>
              <EmissionPlan />
            </Layout>
          }
        />
        <Route
          path={Routes.CAMPAIGNS.NEW}
          element={
            <PrivateRoute allowed={allowedFor({ template: PAGES.CAMPAIGNS.ADD_EDIT })}>
              <CampaignForm formType={FormType.NEW} />
            </PrivateRoute>
          }
        />
        <Route
          path={Routes.CAMPAIGNS.EDIT(':id')}
          element={
            <PrivateRoute allowed={allowedFor({ template: PAGES.CAMPAIGNS.ADD_EDIT })}>
              <CampaignForm formType={FormType.EDIT} />
            </PrivateRoute>
          }
        />
        <Route
          path={Routes.CAMPAIGNS.ACCEPTANCE_FIELDS(':id')}
          element={
            <PrivateRoute allowed={allowedFor({ template: PAGES.CAMPAIGNS.ACCEPTANCE_FIELDS })}>
              <CampaignAcceptanceFields />
            </PrivateRoute>
          }
        />
        <Route
          path={Routes.CAMPAIGNS.EMISSION_SCHEDULE({
            campaignId: ':campaignId',
            creationId: ':creationId',
          })}
        >
          <Route
            path={''}
            element={EmissionSchedulePrivateRoute}
          />
          <Route
            path={':readOnly'}
            element={EmissionSchedulePrivateRoute}
          />
        </Route>
        <Route
          path={Routes.CAMPAIGNS.EMISSION_SCHEDULE_PUBLIC({
            campaignUuid: ':campaignUuid',
            creationUuid: ':creationUuid',
          })}
          element={
            <Layout>
              <EmissionSchedule />
            </Layout>
          }
        />
      </>
    )
  }

  const UserRoutes = () => (
    <>
      <Route
        path={Routes.USERS.ALL}
        element={
          <PrivateRoute
            allowed={allowedFor({
              template: PAGES.USERS.LIST,
              feature: { name: FeatureName.Users, action: PermissionType.Read },
            })}
          >
            <UserTable />
          </PrivateRoute>
        }
      />
      <Route
        path={Routes.USERS.SETTINGS}
        element={
          <PrivateRoute allowed={allowedFor({ template: PAGES.USERS.SETTINGS })}>
            <UserSettings />
          </PrivateRoute>
        }
      />
      <Route
        path={Routes.USERS.EDIT(':id')}
        element={
          <PrivateRoute
            allowed={allowedFor({
              template: PAGES.USERS.EDIT,
              feature: { name: FeatureName.Users, action: PermissionType.Write },
            })}
          >
            <UserEdit />
          </PrivateRoute>
        }
      />
      <Route
        path={Routes.USERS.INVITE}
        element={
          <PrivateRoute
            allowed={allowedFor({
              template: PAGES.USERS.INVITE,
              feature: { name: FeatureName.Users, action: PermissionType.Write },
            })}
          >
            <UserInvite />
          </PrivateRoute>
        }
      />
    </>
  )

  const RoleRoutes = () => (
    <>
      <Route
        path={Routes.ROLES.ALL}
        element={
          <PrivateRoute
            allowed={allowedFor({
              template: PAGES.ROLES.LIST,
              feature: { name: FeatureName.Users, action: PermissionType.Read },
            })}
          >
            <RolesTable />
          </PrivateRoute>
        }
      />
      <Route
        path={Routes.ROLES.NEW}
        element={
          <PrivateRoute
            allowed={allowedFor({
              template: PAGES.ROLES.ADD_EDIT,
              feature: { name: FeatureName.Users, action: PermissionType.Write },
            })}
          >
            <RoleForm />
          </PrivateRoute>
        }
      />
      <Route
        path={Routes.ROLES.EDIT(':id')}
        element={
          <PrivateRoute
            allowed={allowedFor({
              template: PAGES.ROLES.ADD_EDIT,
              feature: { name: FeatureName.Users, action: PermissionType.Write },
            })}
          >
            <RoleForm />
          </PrivateRoute>
        }
      />
    </>
  )

  const BundleRoutes = () => (
    <>
      <Route
        path={Routes.BUNDLES}
        element={
          <PrivateRoute
            allowed={allowedFor({
              template: PAGES.BUNDLES,
            })}
          >
            <Bundles />
          </PrivateRoute>
        }
      />
    </>
  )

  const OtherRoutes = () => (
    <>
      <Route
        path={Routes.REPORT}
        element={
          <PrivateRoute
            allowed={allowedFor({
              template: PAGES.REPORT,
              feature: { name: FeatureName.Reports, action: PermissionType.Active },
            })}
          >
            <Report />
          </PrivateRoute>
        }
      />
      <Route
        path={Routes.BROADSIGN}
        element={
          <PrivateRoute
            allowed={allowedFor({
              template: PAGES.BROADSIGN,
              feature: { name: FeatureName.BroadsignCreations, action: PermissionType.Active },
            })}
          >
            <Broadsign />
          </PrivateRoute>
        }
      />
      <Route
        path={Routes.INVENTORY}
        element={
          <PrivateRoute
            allowed={allowedFor({
              template: PAGES.INVENTORY,
              feature: { name: FeatureName.Inventory, action: PermissionType.Active },
            })}
          >
            <Inventory />
          </PrivateRoute>
        }
      />
      <Route
        path={Routes.FEATURES}
        element={
          <PrivateRoute allowed={allowedFor({ template: PAGES.FEATURES })}>
            <Features />
          </PrivateRoute>
        }
      />
      <Route
        path={Routes.AI_CREATION_UPLOAD}
        element={
          <PrivateRoute
            allowed={allowedFor({
              template: PAGES.AI_CREATIONS,
              feature: { name: FeatureName.AiCreations, action: PermissionType.Active },
            })}
          >
            <AICreationUpload />
          </PrivateRoute>
        }
      />
    </>
  )

  const VoohCampaignRoutes = () => {
    const EmissionSchedulePrivateRoute: ReactElement = (
      <PrivateRoute allowed={allowedFor({ template: PAGES.CAMPAIGNS.EMISSION_SCHEDULE })}>
        <VoohEmissionSchedule />
      </PrivateRoute>
    )

    return (
      <>
        <Route
          path={Routes.VOOH_CAMPAIGNS.ALL}
          element={
            <PrivateRoute allowed={allowedFor({ template: PAGES.VOOH })}>
              <VoohCampaignTable />
            </PrivateRoute>
          }
        />
        <Route
          path={Routes.CAMPAIGNS.EMISSION_SCHEDULE({
            campaignId: ':campaignId',
            creationId: ':creationId',
            vooh: true,
          })}
        >
          <Route
            path={''}
            element={EmissionSchedulePrivateRoute}
          />
          <Route
            path={':readOnly'}
            element={EmissionSchedulePrivateRoute}
          />
        </Route>
        <Route
          path={Routes.CAMPAIGNS.EMISSION_SCHEDULE_PUBLIC({
            campaignUuid: ':campaignUuid',
            creationUuid: ':creationUuid',
            vooh: true,
          })}
          element={
            <Layout>
              <VoohEmissionSchedule />
            </Layout>
          }
        />
        <Route
          path={Routes.VOOH_CAMPAIGNS.NEW}
          element={
            <PrivateRoute allowed={allowedFor({ template: PAGES.VOOH })}>
              <VoohCampaignForm />
            </PrivateRoute>
          }
        />
        <Route
          path={Routes.VOOH_CAMPAIGNS.ONE(':id')}
          element={
            <PrivateRoute allowed={allowedFor({ template: PAGES.VOOH })}>
              <VoohCampaignDetails />
            </PrivateRoute>
          }
        />
        <Route
          path={Routes.VOOH_CAMPAIGNS.ONE_PUBLIC(':uuid')}
          element={
            <Layout>
              <VoohCampaignDetails />
            </Layout>
          }
        />
        <Route
          path={Routes.VOOH_CAMPAIGNS.EDIT(':id')}
          element={
            <PrivateRoute allowed={allowedFor({ template: PAGES.VOOH })}>
              <VoohCampaignForm />
            </PrivateRoute>
          }
        />
      </>
    )
  }

  const RouterContainer = (
    <BrowserRouter>
      <GoogleAnalytics />
      <ScrollToTop />
      <ReactRoutes>
        {UtilityRoutes()}
        {SignRoutes()}
        {AgencyRoutes()}
        {CampaignRoutes()}
        {UserRoutes()}
        {RoleRoutes()}
        {BundleRoutes()}
        {OtherRoutes()}
        {VoohCampaignRoutes()}
      </ReactRoutes>
    </BrowserRouter>
  )

  return (
    <>
      {userData.fetchComplete ? RouterContainer : <FullScreenSpinner />}
      {notifications.length > 0 ? <NotifierQueue /> : null}
    </>
  )
}

export default App
